// Per-client complaint / grievance log (HFRD Rule 111-8-65-.12(3)).
// client_complaints is keyed by client UUID. Complaints must be addressed in a
// timely manner — the row flags anything past its 7-day due date while open.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import DataTable from '../DataTable';

const card = { background: '#fff', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 16 };
const fmt = (d) => d ? new Date(d).toLocaleDateString('en-US') : '—';
const SOURCES = ['Client', 'Family / Responsible Party', 'Staff', 'Other'];
const CATEGORIES = ['Care Quality', 'Staff Conduct', 'Scheduling', 'Billing', 'Safety', 'Other'];
const STATUS_STYLE = {
  open: { bg: 'var(--danger-bg)', fg: 'var(--danger)' },
  investigating: { bg: 'var(--warn-bg)', fg: 'var(--warn)' },
  resolved: { bg: 'var(--success-bg)', fg: 'var(--success)' },
};
const EMPTY = { date_received: new Date().toISOString().slice(0, 10), source: 'Client', category: 'Care Quality', description: '', severity: 'medium' };

export default function ClientComplaintsTab({ client }) {
  const { profile } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);      // null | 'add' | complaintRow (resolve)
  const [form, setForm] = useState(EMPTY);
  const [resolution, setResolution] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('client_complaints').select('*')
      .eq('client_id', client.id).order('date_received', { ascending: false });
    setRows(data || []); setLoading(false);
  }, [client.id]);
  useEffect(() => { load(); }, [load]);

  const overdue = (r) => r.status !== 'resolved' && r.due_date && new Date(r.due_date) < new Date();

  const addComplaint = async () => {
    if (!form.description.trim()) return;
    setBusy(true);
    await supabase.from('client_complaints').insert([{
      client_id: client.id, date_received: form.date_received, source: form.source,
      category: form.category, description: form.description.trim(), severity: form.severity,
      status: 'open', reported_by: profile?.full_name || null,
    }]);
    setBusy(false); setModal(null); setForm(EMPTY); load();
  };

  const resolve = async () => {
    setBusy(true);
    await supabase.from('client_complaints').update({
      status: 'resolved', resolution: resolution.trim() || null,
      resolved_date: new Date().toISOString().slice(0, 10), resolved_by: profile?.full_name || null,
    }).eq('id', modal.id);
    setBusy(false); setModal(null); setResolution(''); load();
  };

  const columns = useMemo(() => [
    { id: 'reference_no', header: 'Ref #', size: 88, enableSorting: false, meta: { mobile: 'secondary' }, cell: i => <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text2)' }}>{i.row.original.reference_no || '—'}</span> },
    { id: 'date_received', header: 'Received', size: 110, enableSorting: false, meta: { mobile: 'primary' }, cell: i => fmt(i.row.original.date_received) },
    { id: 'category', header: 'Category', size: 130, enableSorting: false, meta: { mobile: 'primary' }, cell: i => i.row.original.category || '—' },
    { id: 'description', header: 'Description', size: 260, enableSorting: false, cell: i => <span style={{ whiteSpace: 'normal' }}>{i.row.original.description}</span> },
    { id: 'status', header: 'Status', size: 120, enableSorting: false, meta: { mobile: 'primary' }, cell: i => {
        const r = i.row.original; const st = STATUS_STYLE[r.status] || STATUS_STYLE.open;
        return (
          <span>
            <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: st.bg, color: st.fg }}>{r.status}</span>
            {overdue(r) && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--danger)', fontWeight: 600 }}>⚠ overdue</span>}
          </span>
        );
      } },
    { id: 'actions', header: '', size: 110, enableSorting: false, meta: { mobile: 'secondary' }, cell: i => {
        const r = i.row.original;
        return r.status !== 'resolved'
          ? <button onClick={() => { setModal(r); setResolution(''); }} style={btnSm}>Resolve</button>
          : <span style={{ fontSize: 11, color: 'var(--text3)' }}>{fmt(r.resolved_date)}</span>;
      } },
  ], []);

  const openCount = rows.filter(r => r.status !== 'resolved').length;

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <h3 style={{ margin: 0 }}>Complaints Log {openCount > 0 && <span style={{ fontSize: 12, color: 'var(--danger)' }}>· {openCount} open</span>}</h3>
        <button onClick={() => { setForm(EMPTY); setModal('add'); }} style={{ background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 12px', cursor: 'pointer' }}>+ Log Complaint</button>
      </div>
      <p style={{ fontSize: 12, color: 'var(--text3)', margin: '0 0 10px' }}>Rule 111-8-65-.12(3) — complaints must be addressed in a timely manner (7-day target).</p>
      <DataTable tableId="client-complaints" columns={columns} data={rows} getRowId={r => r.id}
        loading={loading} emptyMessage="No complaints logged for this client." showColumnPicker={false} maxHeight="none" />

      {modal === 'add' && (
        <Modal title="Log a Complaint" onClose={() => setModal(null)} onSave={addComplaint} busy={busy} saveLabel="Log Complaint" saveDisabled={!form.description.trim()}>
          <Row><Fld label="Date Received"><input type="date" value={form.date_received} onChange={e => setForm(f => ({ ...f, date_received: e.target.value }))} style={inp} /></Fld>
            <Fld label="Source"><select value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))} style={inp}>{SOURCES.map(o => <option key={o}>{o}</option>)}</select></Fld></Row>
          <Row><Fld label="Category"><select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} style={inp}>{CATEGORIES.map(o => <option key={o}>{o}</option>)}</select></Fld>
            <Fld label="Severity"><select value={form.severity} onChange={e => setForm(f => ({ ...f, severity: e.target.value }))} style={inp}><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option></select></Fld></Row>
          <Fld label="Description"><textarea rows={4} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} style={{ ...inp, resize: 'vertical' }} placeholder="What was the complaint?" /></Fld>
        </Modal>
      )}
      {modal && modal !== 'add' && (
        <Modal title="Resolve Complaint" onClose={() => setModal(null)} onSave={resolve} busy={busy} saveLabel="Mark Resolved">
          <p style={{ fontSize: 13, color: 'var(--text2)', marginTop: 0 }}>{modal.description}</p>
          <Fld label="Resolution / Action Taken"><textarea rows={4} value={resolution} onChange={e => setResolution(e.target.value)} style={{ ...inp, resize: 'vertical' }} placeholder="How was it resolved?" /></Fld>
        </Modal>
      )}
    </div>
  );
}

const inp = { width: '100%', padding: 8, border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit' };
const btnSm = { background: 'none', border: '1px solid var(--accent)', color: 'var(--accent)', borderRadius: 5, padding: '4px 10px', cursor: 'pointer', fontSize: 12 };
const Row = ({ children }) => <div style={{ display: 'flex', gap: 12 }}>{children}</div>;
const Fld = ({ label, children }) => <div style={{ flex: 1, marginBottom: 10 }}><label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{label}</label>{children}</div>;
function Modal({ title, children, onClose, onSave, busy, saveLabel, saveDisabled }) {
  return (
    <div onMouseDown={e => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
      <div style={{ background: 'var(--card-bg)', borderRadius: 12, padding: 20, width: 460, maxWidth: '92vw', maxHeight: '88vh', overflowY: 'auto' }}>
        <h3 style={{ marginTop: 0 }}>{title}</h3>
        {children}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
          <button onClick={onClose} style={{ background: 'none', border: '1px solid var(--border2)', borderRadius: 6, padding: '8px 14px', cursor: 'pointer' }}>Cancel</button>
          <button onClick={onSave} disabled={busy || saveDisabled} style={{ background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 14px', cursor: busy || saveDisabled ? 'not-allowed' : 'pointer', opacity: busy || saveDisabled ? 0.6 : 1 }}>{busy ? 'Saving…' : (saveLabel || 'Save')}</button>
        </div>
      </div>
    </div>
  );
}
